import { useState } from 'react';
import { ArrowRight, BookOpen, CheckCircle2 } from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { entry } from '@/routes';
import { signInWithEmail } from '@/lib/platform/auth';
import { enterAfterAuth } from '@/lib/platform/routing';

export function SignInPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignIn = async () => {
    setMessage(null);
    setIsSubmitting(true);
    const result = await signInWithEmail(email, password);
    if (!result.ok) {
      setMessage(result.message);
      setIsSubmitting(false);
      return;
    }
    await enterAfterAuth(entry);
  };

  return (
    <main className="min-h-screen bg-background text-foreground selection:bg-primary/20">
      <div className="mx-auto grid min-h-screen w-full max-w-6xl grid-cols-1 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="relative hidden overflow-hidden border-r border-border/70 px-10 py-12 lg:flex lg:flex-col lg:justify-between xl:px-16">
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_18%_12%,color-mix(in_oklch,var(--secondary)_65%,transparent),transparent_38%)]" />
          <div className="flex items-center gap-3 text-sm font-semibold tracking-tight">
            <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
              <BookOpen className="size-4" aria-hidden="true" />
            </span>
            <span>Hive Register</span>
          </div>
          <div className="max-w-md pb-8">
            <p className="mb-5 text-sm font-medium text-primary">The working register for your apiary</p>
            <h1 className="max-w-lg text-4xl font-semibold tracking-[-0.035em] text-balance xl:text-5xl">
              Keep every hive, receipt, and season in one clear place.
            </h1>
            <p className="mt-6 max-w-md text-base leading-7 text-muted-foreground">
              Sign in to continue your registry work. Your account keeps the paper trail close when the hives are not.
            </p>
            <div className="mt-10 grid gap-4 border-t border-border/70 pt-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
                Record the latest inspection
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="size-4 text-primary" aria-hidden="true" />
                Keep national receipts together
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">A steady register for careful work.</p>
        </section>

        <section className="flex min-h-screen items-center justify-center px-6 py-12 sm:px-10">
          <div className="w-full max-w-sm">
            <div className="mb-10 lg:hidden">
              <div className="flex items-center gap-3 text-sm font-semibold tracking-tight">
                <span className="flex size-9 items-center justify-center rounded-xl bg-primary text-primary-foreground">
                  <BookOpen className="size-4" aria-hidden="true" />
                </span>
                Hive Register
              </div>
            </div>
            <div className="mb-8">
              <h2 className="text-3xl font-semibold tracking-[-0.03em]">Welcome back</h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">Sign in to open your register.</p>
            </div>

            <div className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="sign-in-email">Email address</Label>
                <Input
                  id="sign-in-email"
                  data-jr-key="sign-in-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sign-in-password">Password</Label>
                <Input
                  id="sign-in-password"
                  data-jr-key="sign-in-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              {message ? (
                <p
                  role="alert"
                  className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive"
                >
                  {message}
                </p>
              ) : null}
              <Button
                data-jr-key="sign-in-submit"
                className="h-10 w-full"
                disabled={isSubmitting}
                onClick={handleSignIn}
              >
                {isSubmitting ? 'Opening register…' : 'Sign in'}
                {!isSubmitting ? <ArrowRight className="ml-1 size-4" aria-hidden="true" /> : null}
              </Button>
            </div>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              New to Hive Register?{' '}
              <Link
                data-jr-key="sign-up-link"
                to="/sign-up"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                Create an account
              </Link>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
}
